import { useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { generateUUID } from '@/shared/utils/uuid';
import { useRealtimeAgentStore } from '@/shared/store/realtime-agent';
import { AgentConnectionManager } from './use-agent-connection';
import type {
  ClientMessage,
} from '../types/websocket-messages';
import { interruptActiveTurn, startLatestTurn } from './turn-lifecycle';
import { latestUserActionGuard } from '@/shared/lib/latest-user-action-guard';


// 이 모듈은 AgentConnectionManager의 싱글톤 WebSocket을 재사용합니다.
// 연결 초기화는 useAgentConnection에서 담당합니다.

// 액션만 필요한 경우를 위한 훅 (리렌더링 없음)
// 연결 초기화는 useAgentConnection에서 담당하며, 이 훅은 해당 연결을 재사용합니다.
export const useAgentWebsocketActions = () => {
  // 액션들을 한 번만 가져와서 재사용 (리렌더링 방지)
  const actionsRef = useRef<{
    setCurrentTurnId: (turnId: string | null) => void;
    setLoading: (loading: boolean) => void;
    setInterrupted: (interrupted: boolean) => void;
    clearChunks: () => void;
    clearTextChunks: () => void;
    clearSttResult: () => void;
    clearCurrentTurn: () => void;
    reset: () => void;
  } | null>(null);

  if (!actionsRef.current) {
    // 스토어에서 액션들만 직접 가져오기 (구독 없음)
    const store = useRealtimeAgentStore.getState();
    actionsRef.current = {
      setCurrentTurnId: store.setCurrentTurnId,
      setLoading: store.setLoading,
      setInterrupted: store.setInterrupted,
      clearChunks: store.clearChunks,
      clearTextChunks: store.clearTextChunks,
      clearSttResult: store.clearSttResult,
      clearCurrentTurn: store.clearCurrentTurn,
      reset: store.reset
    };
  }

  const actions = actionsRef.current;

  // WebSocket 메시지 전송 함수 - AgentConnectionManager 싱글톤 사용
  const sendMessage = (message: ClientMessage) => {
    const api = AgentConnectionManager.getAPI();

    // 새로운 턴 ID 생성 (메시지에 없을 경우 사용)
    const fallbackTurnId = generateUUID();

    // session_id와 turn_id가 필요한 메시지들에 자동으로 추가
    if ('data' in message && message.data && 'session_id' in message.data && 'turn_id' in message.data) {
      // session_id가 비어있으면 API의 session_id 사용
      if (!message.data.session_id && api?.isConnected) {
        message.data.session_id = api.currentSessionId || '';
      }
      // turn_id가 비어있으면 새로 생성된 turn_id 사용
      if (!message.data.turn_id) {
        message.data.turn_id = fallbackTurnId;
      }
    }

    // 기존 턴 데이터 클리어하고 실제 사용할 turnId 설정
    if (message.type === "message") {
      latestUserActionGuard.begin();
      const actualTurnId = ('data' in message && message.data && 'turn_id' in message.data)
        ? message.data.turn_id
        : fallbackTurnId;
      const store = useRealtimeAgentStore.getState();

      return startLatestTurn({
        state: store,
        getCurrentTurnId: () => useRealtimeAgentStore.getState().currentTurnId,
        turnId: actualTurnId,
        sendInterrupt: (turnId) => api?.isConnected
          ? api.sendInterrupt(turnId)
          : false,
        sendMessage: (turnId) => {
          if (!api?.isConnected) return false;
          if ('data' in message && message.data && 'turn_id' in message.data) {
            message.data.turn_id = turnId;
          }
          return api.send(message);
        },
      });
    }

    // AgentWebSocketAPI의 send 메서드 사용
    if (!api?.isConnected) {
      console.warn('🚫 WebSocket이 연결되지 않았습니다.');
      return false;
    }
    return api.send(message);
  };

  // Interrupt 메시지 전송 함수
  const sendInterrupt = () => {
    const api = AgentConnectionManager.getAPI();
    const currentTurnId = useRealtimeAgentStore.getState().currentTurnId;

    if (!api?.isConnected) {
      console.warn('🚫 Interrupt 전송 실패: WebSocket 연결 없음');
      return false;
    }

    if (!currentTurnId) {
      console.warn('🚫 Interrupt 전송 실패: 턴 ID 없음');
      return false;
    }

    console.log('⏸️ WS: Interrupt 메시지 전송');
    return interruptActiveTurn({
      state: useRealtimeAgentStore.getState(),
      getCurrentTurnId: () => useRealtimeAgentStore.getState().currentTurnId,
      sendInterrupt: (turnId) => api.sendInterrupt(turnId),
    });
  };

  // 명시적 연결 해제 함수 - AgentConnectionManager 사용
  const disconnect = () => {
    AgentConnectionManager.reset();
    actions.reset();
  };

  // 재연결 함수
  const reconnect = async () => {
    disconnect();
    // useAgentConnection이 다시 초기화할 것임
    return Promise.resolve();
  };

  // 연결 보장 함수
  const ensureConnection = async (): Promise<void> => {
    if (AgentConnectionManager.isConnected()) {
      return Promise.resolve();
    }
    // useAgentConnection이 연결을 관리하므로 여기서는 대기만 함
    return new Promise((resolve) => {
      const checkConnection = () => {
        if (AgentConnectionManager.isConnected()) {
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      checkConnection();
    });
  };

  return {
    // 액션들 (리렌더링 없이 사용 가능)
    sendMessage,
    sendInterrupt,
    disconnect,
    reconnect,
    ensureConnection,
    clearChunks: actions.clearChunks,
    clearTextChunks: actions.clearTextChunks,
    clearSttResult: actions.clearSttResult,
    clearCurrentTurn: actions.clearCurrentTurn,

    // 원시 WebSocket 인스턴스 (AgentConnectionManager에서 가져옴)
    webSocketService: null, // deprecated - AgentConnectionManager.getAPI() 사용
  };
};

// 연결 상태만 필요한 경우
export const useAgentConnectionStatus = () => {
  return useRealtimeAgentStore((state) => state.isConnected);
};

// 세션 정보만 필요한 경우
export const useAgentSession = () => {
  return useRealtimeAgentStore(
    useShallow((state) => ({
      sessionId: state.sessionId,
      isConnected: state.isConnected,
    }))
  );
};

// 청크 데이터만 필요한 경우
export const useAgentChunks = () => {
  return useRealtimeAgentStore(
    useShallow((state) => ({
      chunks: state.chunks,
      textChunks: state.textChunks,
      currentTurnId: state.currentTurnId,
    }))
  );
};

// STT 결과만 필요한 경우
export const useAgentSttResult = () => {
  return useRealtimeAgentStore((state) => state.currentSttResult);
};

// 에러 상태만 필요한 경우
export const useAgentError = () => {
  return useRealtimeAgentStore(
    useShallow((state) => ({
      lastError: state.lastError,
      isInterrupted: state.isInterrupted,
      isLoading: state.isLoading,
    }))
  );
};

// 로딩 상태만 필요한 경우
export const useAgentLoading = () => {
  return useRealtimeAgentStore((state) => state.isLoading);
};

// 모든 상태가 필요한 경우 (기존 호환성 유지) - 단일 구독으로 최적화
export const useAgentWebsocket = () => {
  // 모든 필요한 상태를 한 번에 구독 (리렌더링 최소화)
  const state = useRealtimeAgentStore(
    useShallow((state) => ({
      isConnected: state.isConnected,
      sessionId: state.sessionId,
      textChunks: state.textChunks,
      chunks: state.chunks,
      currentSttResult: state.currentSttResult,
      currentTurnId: state.currentTurnId,
      lastError: state.lastError,
      isInterrupted: state.isInterrupted,
      isLoading: state.isLoading,
    }))
  );

  // 액션들 가져오기 (리렌더링 없음)
  const actions = useAgentWebsocketActions();

  return {
    // 상태
    ...state,

    // 액션
    sendMessage: actions.sendMessage,
    sendInterrupt: actions.sendInterrupt,
    disconnect: actions.disconnect,
    reconnect: actions.reconnect,
    ensureConnection: actions.ensureConnection,
    clearChunks: actions.clearChunks,
    clearTextChunks: actions.clearTextChunks,
    clearSttResult: actions.clearSttResult,
    clearCurrentTurn: actions.clearCurrentTurn,

    // 원시 WebSocket 인스턴스 (필요시)
    webSocketService: actions.webSocketService,
  };
};

// 전역 연결 관리 유틸리티 (훅 외부에서 사용 가능)
// AgentConnectionManager로 위임 (호환성 유지용)
export const AgentWebSocketManager = {
  // 연결 상태 확인
  isConnected: () => AgentConnectionManager.isConnected(),

  // 연결 대기 중 상태 확인
  isConnecting: () => AgentConnectionManager.isInitializing(),

  // 연결 보장 (Promise 기반) - 실제 초기화는 useAgentConnection에서 담당
  ensureConnection: async (): Promise<void> => {
    if (AgentConnectionManager.isConnected()) {
      return Promise.resolve();
    }
    // useAgentConnection이 연결을 관리하므로 여기서는 대기만 함
    return new Promise((resolve) => {
      const checkConnection = () => {
        if (AgentConnectionManager.isConnected()) {
          resolve();
        } else {
          setTimeout(checkConnection, 100);
        }
      };
      checkConnection();
    });
  },

  // 연결 해제
  disconnect: () => {
    AgentConnectionManager.reset();
  }
};
